import React, { FC } from 'react';
import { get, toLower } from 'lodash';
import {
  Box,
  SpinnerSize,
  SideSheet,
  SideSheetHeader,
  SideSheetClose,
  SideSheetBody,
  SideSheetSubHeader,
  SideSheetSize,
  SideSheetContentCol,
  Spinner,
  theme,
} from '@bechtel/gds';
import { GET_COMMODITY_DETAIL } from '@bechtel/client-gql';
import {
  CommodityWrapper,
  CommodityReference,
  MaterialCommodity,
  Weld,
  CommodityMilestoneActivity,
  PagingInput,
} from '@bechtel/types';
import { TitleValue, ErrorRetry, ActivityHistory } from '@bechtel/client-common';
import { NetworkStatus, useQuery } from '@apollo/client';
import { t, Trans } from '@lingui/macro';
import { CommodityDetailViewProps } from './types';
import { SpecificationDetailView } from '~/components/molecules/specification-detail';

export const CommodityDetail: FC<CommodityDetailViewProps> = ({
  commodityId,
  commodityName,
  commodityTag,
  workPackageTag,
  close,
  showDocuments = false,
}) => {
  const activityLimit = 10;
  const { loading, error, data, refetch, networkStatus, fetchMore } = useQuery<
    { commodity: CommodityWrapper },
    { input: CommodityReference; paging: PagingInput }
  >(GET_COMMODITY_DETAIL, {
    variables: {
      input: {
        commodityId: commodityId,
      },
      paging: {
        cursor: '1',
        limit: activityLimit,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const materialCommodity = get(data, 'commodity.materialCommodity');
  const commodity: MaterialCommodity | Weld = materialCommodity ? materialCommodity : get(data, 'commodity.weld');
  const milestonesActivityHistory: CommodityMilestoneActivity[] = get(commodity, 'milestonesActivityHistory.items', []);
  const totalActivities = get(commodity, 'milestonesActivityHistory.total', 0);
  const totalPages = Math.ceil(totalActivities / activityLimit);
  const activityCursor = get(commodity, 'milestonesActivityHistory.cursor', 1);
  const isWeldCommodity = toLower(commodity?.type?.name) === 'weld';
  const sortedMilestonesActivityHistory = milestonesActivityHistory
    ? [...milestonesActivityHistory]?.sort(
        (a, b) => new Date(b?.dateTime)?.getTime() - new Date(a?.dateTime)?.getTime() || b?.orderBy - a?.orderBy
      )
    : [];

  const fetchResult = () => {
    fetchMore({
      variables: {
        paging: {
          cursor: activityCursor.toString(),
          limit: activityLimit,
        },
      },
    });
  };

  const handleScroll = (e) => {
    if (e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight && activityCursor <= totalPages) {
      if (!loading) {
        fetchResult();
      }
    }
  };
  //TODO: user rights wireup
  //const {user, hasRight} = useAppUser();
  //TODO when logic change check test case as well
  const hasRightsToSeeActivityView = true; //hasRight(Rights.CommodityActivity);

  return (
    <SideSheet onDismiss={close} size={SideSheetSize.MEDIUM} visible data-testid="commodity-detail-sidesheet">
      <SideSheetHeader title={`${commodityName} ${t`Details`}`}>
        <SideSheetClose onClick={close}></SideSheetClose>
      </SideSheetHeader>
      <SideSheetSubHeader>
        <Box mt={{ sm: '400' }} data-testid="commodity-header">
          <TitleValue title={t`Work Package`} value={workPackageTag} size="500" />
        </Box>
        <Box mt={{ sm: '400' }}>
          <TitleValue title={commodityName} value={commodityTag} size="500" />
        </Box>
      </SideSheetSubHeader>
      <SideSheetBody>
        {hasRightsToSeeActivityView && (
          <SideSheetContentCol
            onScroll={(e) => {
              handleScroll(e);
            }}
          >
            {sortedMilestonesActivityHistory && sortedMilestonesActivityHistory.length > 0 && (
              <ActivityHistory
                activityTitle={t`Activity`}
                milestonesActivityHistory={sortedMilestonesActivityHistory}
                isWeldCommodity={isWeldCommodity}
                commodityName={commodityName}
                commodityTag={commodityTag}
                workPackageTag={workPackageTag}
              />
            )}
            {(loading || networkStatus === NetworkStatus.refetch) && (
              <Box css={!sortedMilestonesActivityHistory ? 'height: 100%;' : ''}>
                <Box
                  data-testid="activity-loader"
                  flexGrow={{ sm: 1 }}
                  alignItems={{ sm: 'center' }}
                  justifyContent={{ sm: 'center' }}
                  p={{ sm: '500' }}
                >
                  <Spinner size={SpinnerSize.LARGE} />
                </Box>
              </Box>
            )}
            {!loading && error && (
              <Box data-testid="commodity-activity-error">
                <ErrorRetry
                  refetch={() =>
                    refetch({
                      input: {
                        commodityId: commodityId,
                      },
                      paging: { cursor: '0', limit: activityLimit },
                    })
                  }
                >
                  <Trans>
                    There was a problem loading commodity activity details. Please click the Retry button below to try
                    again.
                  </Trans>
                </ErrorRetry>
              </Box>
            )}
            {!loading && !error && (!milestonesActivityHistory || milestonesActivityHistory.length === 0) && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                p={{ sm: '500' }}
                data-testid="no-commodity-activity"
              >
                <Trans>No commodity activity details found.</Trans>
              </Box>
            )}
          </SideSheetContentCol>
        )}
        <SideSheetContentCol style={{ borderLeft: `1px solid ${theme.colors.grey[300]}` }}>
          {(loading || networkStatus === NetworkStatus.refetch) && !commodity && (
            <Box css="height: 100%;">
              <Box
                flexGrow={{ sm: 1 }}
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                p={{ sm: '500' }}
                data-testid="commodity-detail-loader"
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            </Box>
          )}
          {!loading && error && (
            <Box data-testid="commodity-detail-error">
              <ErrorRetry
                refetch={() =>
                  refetch({
                    input: {
                      commodityId: commodityId,
                    },
                    paging: { cursor: '0', limit: activityLimit },
                  })
                }
              >
                <Trans>
                  There was a problem loading commodity details. Please click the Retry button below to try again.
                </Trans>
              </ErrorRetry>
            </Box>
          )}
          {!loading && !error && (!commodity || !commodity.sections) && (
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }}>
              <Trans data-testid="commodity-details-not-found">No commodity details found.</Trans>
            </Box>
          )}

          {commodity && (
            <SpecificationDetailView
              isoDocument={commodity.isoDocument}
              commoditySections={commodity.sections}
              showDocuments={showDocuments}
              workPackageTag={workPackageTag}
              commodityName={commodityName}
              commodityTag={commodityTag}
            />
          )}
        </SideSheetContentCol>
      </SideSheetBody>
    </SideSheet>
  );
};
